'use client'

import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
	AlertDialog,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { useDeleteTaxExemptions } from '@/lib/api/taxExemptions'
import { useToast } from '@/components/ui/use-toast'

interface BulkDeleteTaxExemptionsModalProps {
	taxExemptionIds: string[]
	onClose: () => void
	onSuccess?: () => void
}

export const BulkDeleteTaxExemptionsModal = ({ taxExemptionIds, onClose, onSuccess }: BulkDeleteTaxExemptionsModalProps) => {
	const { t } = useTranslation('taxes')
	const { t: tCommon } = useTranslation('common')
	const { toast } = useToast()
	const deleteTaxExemptions = useDeleteTaxExemptions()

	const count = taxExemptionIds.length

	const handleDelete = async () => {
		if (taxExemptionIds.length === 0) return

		try {
			await deleteTaxExemptions.mutateAsync({ ids: taxExemptionIds })

			toast({
				title: tCommon('success'),
				description: t('exemptions.deleteBulkSuccess', { count }),
			})

			onSuccess?.()
			onClose()
		} catch (error) {
			toast({
				title: tCommon('error'),
				description: t('exemptions.deleteBulkError'),
				variant: 'destructive',
			})
		}
	}

	return (
		<AlertDialog open={true} onOpenChange={onClose}>
			<AlertDialogContent>
				<AlertDialogHeader>
					<AlertDialogTitle>{t('exemptions.modals.bulkDelete.title')}</AlertDialogTitle>
					<AlertDialogDescription>
						{t('exemptions.modals.bulkDelete.description', { count })}
					</AlertDialogDescription>
				</AlertDialogHeader>
				<AlertDialogFooter>
					<Button variant="outline" onClick={onClose} disabled={deleteTaxExemptions.isPending}>
						{tCommon('cancel')}
					</Button>
					<Button variant="destructive" onClick={handleDelete} disabled={deleteTaxExemptions.isPending}>
						{deleteTaxExemptions.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
						{tCommon('delete')}
					</Button>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	)
}
